import { Component } from 'react'
import * as styles from 'styles/organisms/modules/Breakdown.css'
import * as cn from 'classnames'
import { formatUSCurrency, getMax, formatBreakdownMoney } from 'helpers/formatHelpers'
import { CommonChartValues } from 'interfaces/chart'
import Tooltip from 'components/features/pfm/Tooltip/Tooltip'
import { StatusType } from 'interfaces'
import * as isNumber from 'lodash/isNumber'
import { MINIMUM_BAR_WIDTH } from 'constants/chart'

export type BreakdownChartProps = CommonChartValues & {
  maxTotal?: number
  memberProjected?: number
  peerProjected?: number
  otherProjected?: number
  limit?: number
  limitLabel?: string
  isExpanded?: boolean
  withComparison: boolean
}

type BreakdownBarProps = CommonChartValues & {
  value: number
  projected: number
  theme: StatusType
  hidden?: boolean
}

class BreakdownChart extends Component<BreakdownChartProps, {}> {

  render () {
    const {
      memberValue, peerValue, otherValue,
      memberProjected, peerProjected, otherProjected,
      limit, limitLabel, isExpanded,
      withComparison,
    } = this.props

    const limitIconPosition = this.getBarLength(limit || 0)

    const tooltipPosition = (actual: number, projected: number) => {
      const projectedLength = this.getBarLength(projected)
      const actualLength = this.getBarLength(actual)
      const adjustAmount = (projectedLength - actualLength) / 2
      return projectedLength - adjustAmount + '%'
    }

    const valueLabels = memberProjected || peerProjected || otherProjected ? (
      <div className={ styles.valueLabels }>
        <span className={ styles.actualValuesLabel }>ACTUAL</span>
        <span className={ styles.projectionValuesLabel }>PROJECTED</span>
      </div>
    ) : null

    const breakdowns = [
      { value: memberValue, projected: memberProjected, theme: 'member' },
      { value: peerValue, projected: peerProjected, theme: 'peers', hidden: !withComparison },
      { value: otherValue, projected: otherProjected, theme: 'others', hidden: !withComparison },
    ]

    const renderBreakdownBar = ({ value, projected, theme, hidden }: BreakdownBarProps, idx: number) => {
      const barWidth = this.getBarLength(value)
      const projectedBarWidth = projected ? this.getBarLength(projected) : null

      const actualValue = (
        <span className={ cn(styles.actualValue, styles[theme]) }>
          { formatBreakdownMoney(value) }
        </span>
      )

      const projectionValue = isNumber(projected) ? (
        <span className={ cn(styles.projectionValue, styles[theme], styles.light) }>
          { formatUSCurrency(projected) }
        </span>
      ) : null

      const projectionTooltip = projected ? (
        <div style={{ left: tooltipPosition(value, projected) }} className={ styles.projectionTooltipContainer }>
          <Tooltip theme={ theme } light>
            projected: { formatUSCurrency(projected) }
          </Tooltip>
        </div>
      ) : null

      const projectionBar = projected ? (
        <div
          style={{ width: projectedBarWidth + '%' }}
          className={ cn(styles.bar, styles.projectionBar, styles[theme], styles.light) }
        />
      ) : null

      const limitIcon = limit && theme === 'member' ? (
        <div
          className={ styles.limitIconWrapper }
          style={{ left: limitIconPosition + '%' }}
        >
          <i className={ styles.limitIcon } />
          <div className={ styles.tooltipContainer }>
            <Tooltip theme={ theme }>{ `${ limitLabel }: ${ formatUSCurrency(limit) }` }</Tooltip>
          </div>
        </div>
      ) : null

      return (
        <div key={ idx } className={ cn(styles.barContainer, { [styles.expanded]: isExpanded, [styles.hidden]: hidden }) }>
          { actualValue }
          <div
            style={{ width: barWidth + '%' }}
            className={ cn(styles.bar, styles[theme]) }
          />
          { projectionBar }
          { projectionValue }
          { projectionTooltip }
          { limitIcon }
        </div>
      )
    }

    return (
      <figure className={ styles.breakdownChart } data-qa-marker='breakdown-section'>
        { valueLabels }
        { breakdowns.map(renderBreakdownBar) }
      </figure>
    )
  }

  private getBarLength = (amount: number) => {
    const {
      memberValue, peerValue, otherValue,
      memberProjected, peerProjected, otherProjected,
      maxTotal,
    } = this.props

    const maxAmount = getMax([memberValue, peerValue, otherValue])
    const maxProjected = getMax([memberProjected, peerProjected, otherProjected])
    const maxValue = getMax([maxAmount, maxProjected])
    const max = maxTotal ? maxTotal : maxValue

    const length = (amount * 100) / max
    if (amount === 0 || length < MINIMUM_BAR_WIDTH) {
      return MINIMUM_BAR_WIDTH
    }
    return max === amount ? 100 : length
  }
}

export default BreakdownChart
